import React, { useCallback, useEffect, useMemo, useState } from "react";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import ErrorHandler from "@components/ErrorHandler/ErrorHandler";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import UnauthorizedRedirection from "@components/UnauthorizedRedirection/UnauthorizedRedirection";
import { useCheckOwnersMatter } from "@features/vessel-call/vessel-call-status/hooks/useCheckOwnersMatter.ts";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { getDepartureCallById, getDepartureTaskById } from "@services/departureServiceApi";
import { useQuery } from "@tanstack/react-query";
import { checkPermission } from "@utils/user-permissions";
import { useOutletContext, useParams } from "react-router-dom";
import type { VesselCallStatusContextType } from "../VesselCallStatus";
import DepartureRequest from "./components/DepartureRequest/DepartureRequest";
import DepartureStatus from "./components/DepartureStatus/DepartureStatus";
import type { DepartureStatusDTO } from "./components/DepartureStatus/DepartureStatus.types";
import { FORM_FIELDS } from "./constants";
import mapDepartureToFormData from "./mappers/departureToFormData";
import type { RequestDepartureFormData } from "./schema/requestDepartureFormSchema";
import { useRequestDepartureFormStore } from "./store/useRequestDepartureForm.store";
import { buildDepartureStatus } from "./utils";

const DepartureContainer: React.FC = () => {
  const { id } = useParams();
  const outletContext = useOutletContext<VesselCallStatusContextType | null>();
  const vesselCallDetails = outletContext?.vesselCallDetails;
  const departureAvailability = outletContext?.departureAvailability;
  const refetchDepartureAvailability = outletContext?.refetchDepartureAvailability;

  const { user, userAccessPermissions } = useAuthStore();
  const isTerminalUser = user?.roles?.includes("terminal") ?? false;

  const { resetForm, setAll } = useRequestDepartureFormStore();
  const [isResubmitMode, setIsResubmitMode] = useState(false);
  const isMarineDepartment = checkPermission(userAccessPermissions, "hasTaskAccess");

  const {
    data: departureDetails,
    isLoading: isDepartureLoading,
    isError: isDepartureError,
    error: departureError,
    refetch: departureRefetch,
  } = useQuery({
    queryKey: ["vessel-departure-details", id],
    queryFn: () => getDepartureCallById(id as string),
    enabled: !isTerminalUser,
    staleTime: 0,
  });

  const taskId = departureDetails?.task?.taskId;

  const { data: taskDetails, refetch: taskRefetch } = useQuery({
    queryKey: ["vessel-departure-task-details", taskId],
    queryFn: () => getDepartureTaskById(taskId as string),
    enabled: !isTerminalUser && !!taskId && isMarineDepartment,
    refetchInterval: 60_000,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const details = useMemo(() => {
    return taskDetails ? taskDetails : departureDetails;
  }, [taskDetails, departureDetails]);

  const departureStatus: DepartureStatusDTO = buildDepartureStatus(
    details,
    departureDetails?.status || taskDetails?.status,
  );

  // When there is no departure request created for the vessel call, the BE response is 404
  // In this case, we want to show the departure request form instead of the error handler
  const departureStatusCode =
    (departureError as { response?: { status?: number }; status?: number } | null)?.response
      ?.status ?? (departureError as { status?: number } | null)?.status;
  const isDepartureNotFound = isDepartureError && departureStatusCode === 404;

  const handleResubmit = useCallback(() => {
    resetForm();
    const uploadStore = useUploadStore.getState();
    uploadStore.clearFilesForKey(FORM_FIELDS.PRE_SAILING_MASTER_DECLARATION);
    uploadStore.clearFilesForKey(FORM_FIELDS.DEPARTURE_CREW_LIST);
    uploadStore.clearFilesForKey(FORM_FIELDS.CARGO_MANIFEST);
    uploadStore.clearFilesForKey(FORM_FIELDS.CUSTOMS_CLEARANCE);
    uploadStore.clearFilesForKey(FORM_FIELDS.ADDITIONAL_DOCUMENTS);

    const formData: RequestDepartureFormData = mapDepartureToFormData(details!);
    uploadStore.pushFilesForKey(
      FORM_FIELDS.PRE_SAILING_MASTER_DECLARATION,
      formData.preSailingMasterDeclaration || [],
    );
    uploadStore.pushFilesForKey(FORM_FIELDS.DEPARTURE_CREW_LIST, formData.departureCrewList || []);
    uploadStore.pushFilesForKey(FORM_FIELDS.CARGO_MANIFEST, formData.cargoManifest || []);
    uploadStore.pushFilesForKey(FORM_FIELDS.CUSTOMS_CLEARANCE, []);
    uploadStore.pushFilesForKey(
      FORM_FIELDS.ADDITIONAL_DOCUMENTS,
      formData.additionalDocuments || [],
    );
    setAll(formData);

    setIsResubmitMode(true);
  }, [resetForm, setAll, details]);

  useEffect(() => {
    if (isTerminalUser || !vesselCallDetails) return;

    setAll({
      nextPort: vesselCallDetails.nextPortReferenceCode ?? "",
      finalDestination: vesselCallDetails.finalDestination ?? "",
      draftFore: vesselCallDetails.draftFwd,
      draftAft: vesselCallDetails.draftAft,
    });
  }, [isTerminalUser, vesselCallDetails, setAll]);

  const refetch = useCallback(() => {
    departureRefetch();
    if (taskId && isMarineDepartment) taskRefetch();
    setIsResubmitMode(false);
  }, [departureRefetch, isMarineDepartment, taskId, taskRefetch]);

  const ownersMatter = useCheckOwnersMatter(id || "");

  if (isTerminalUser) {
    return null;
  }

  if (isDepartureLoading) {
    return (
      <div className="absolute inset-0 flex justify-center pointer-events-none top-28 opacity-100">
        <RotateRightIcon
          fontSize="large"
          sx={{
            "animation": "mui-spin 0.9s linear infinite",
            "@keyframes mui-spin": { to: { transform: "rotate(360deg)" } },
          }}
        />
      </div>
    );
  }

  if (isDepartureError && !isDepartureNotFound) {
    return <ErrorHandler error={departureError} testId="departure-error-handler" />;
  }

  if (ownersMatter) {
    return <UnauthorizedRedirection />;
  }

  const lastDepartureWasCancelled = departureDetails?.status === "CANCELLED";
  const hasCancelledDeparture = departureStatus?.hasCancelledDeparture;
  const hadCancelledDepartures = lastDepartureWasCancelled || hasCancelledDeparture;

  if (departureDetails && !isResubmitMode && !lastDepartureWasCancelled) {
    return (
      <DepartureStatus
        departureDetails={departureStatus}
        refetch={refetch}
        onResubmit={handleResubmit}
      />
    );
  }

  return (
    <DepartureRequest
      refetch={refetch}
      isResubmitMode={isResubmitMode}
      isCustomsBypass={details?.workflowStatus === "CUSTOMS_BYPASS_CONFIRMED"}
      task={departureStatus.task}
      status={departureStatus.status}
      vesselId={departureStatus.vesselId}
      submittedBy={departureStatus.submittedBy}
      submittedByRole={departureStatus.submittedByRole}
      hadCancelledDepartures={hadCancelledDepartures}
      departureAvailability={departureAvailability}
      refetchDepartureAvailability={refetchDepartureAvailability}
      initialCustomsClearance={
        departureStatus.customsClearance
          ? {
              name: departureStatus.customsClearance.fileName,
              url: departureStatus.customsClearance.documentUrl,
            }
          : undefined
      }
    />
  );
};

export default DepartureContainer;
